import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import api from '../api/axios';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const fmt = (n) => (n || 0).toLocaleString('en-IN');

function getDateRange(preset) {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  let start;
  switch (preset) {
    case 'today':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case 'week':
      start = new Date(now.getTime() - 6 * 24 * 60 * 60 * 1000);
      start.setHours(0, 0, 0, 0);
      break;
    case 'month':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case 'year':
      start = new Date(now.getFullYear(), 0, 1);
      break;
    default:
      start = new Date(2020, 0, 1);
  }
  return { start, end };
}

function filterByDate(records, dateField, start, end) {
  if (!Array.isArray(records)) return [];
  return records.filter(r => {
    const d = new Date(r[dateField]);
    return d >= start && d <= end;
  });
}

function getTypeLabel(tx) {
  if (tx._source === 'donation') return tx.offeringName || 'Cash Gift';
  if (tx._source === 'activity') return tx.serviceName || 'Spiritual Offering';
  return tx.productName || tx.productId?.name || 'Marketplace Item';
}

function getTypeBadge(source) {
  if (source === 'donation') return { label: 'Donation',  color: '#E8B84B', bg: 'rgba(212,175,55,0.12)',  dot: '🟡' };
  if (source === 'activity') return { label: 'Offering',  color: '#a78bfa', bg: 'rgba(167,139,250,0.12)', dot: '🟣' };
  return                            { label: 'Order',     color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   dot: '🟢' };
}

function getStatusStyle(status) {
  const s = (status || '').toLowerCase();
  if (['settled', 'delivered', 'completed'].includes(s))
    return { bg: 'rgba(34,197,94,0.1)', color: '#22c55e' };
  if (['cancelled', 'failed'].includes(s))
    return { bg: 'rgba(239,68,68,0.1)', color: '#f87171' };
  return { bg: 'rgba(234,179,8,0.1)', color: '#eab308' };
}

// ─────────────────────────────────────────────────────────────────────────────
// Animated Counter
// ─────────────────────────────────────────────────────────────────────────────
const AnimatedCounter = ({ target }) => {
  const [val, setVal] = useState(0);
  const raf = useRef(null);
  const prevTarget = useRef(0);

  useEffect(() => {
    const from = prevTarget.current;
    prevTarget.current = target;
    const duration = 1000;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.round(from + eased * (target - from)));
      if (progress < 1) raf.current = requestAnimationFrame(tick);
    };
    cancelAnimationFrame(raf.current);
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target]);

  return <span>₹{fmt(val)}</span>;
};

// ─────────────────────────────────────────────────────────────────────────────
// Custom Bar Chart Tooltip
// ─────────────────────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(18,5,5,0.97)',
      border: '1px solid rgba(212,175,55,0.25)',
      borderRadius: 10,
      padding: '10px 16px',
    }}>
      <p style={{ color: '#E8B84B', fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{label}</p>
      {payload.map(p => (
        <p key={p.name} style={{ color: p.color, fontSize: 13, fontWeight: 700, marginBottom: 2 }}>
          {p.name}: ₹{fmt(p.value)}
        </p>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Export CSV
// ─────────────────────────────────────────────────────────────────────────────
function safeStr(val) {
  return String(val ?? '').replace(/"/g, '""').replace(/[\r\n]+/g, ' ');
}

function exportCSV(transactions, centreName, preset) {
  // Locale-independent formatter — outputs DD/MM/YYYY HH:MM
  // Tab prefix (\t) tells Excel to treat the cell as text → prevents ### column overflow
  function fmtDateTime(v) {
    if (!v) return 'N/A';
    const d = new Date(v);
    if (isNaN(d.getTime())) return 'N/A';
    const dd   = String(d.getDate()).padStart(2, '0');
    const mm   = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh   = String(d.getHours()).padStart(2, '0');
    const min  = String(d.getMinutes()).padStart(2, '0');
    return `\t${dd}/${mm}/${yyyy} ${hh}:${min}`;   // \t forces text in Excel
  }

  const header = [
    'Date & Time', 'TX ID', 'Type', 'Sub-Type',
    'Devotee / Customer', 'Email', 'Phone',
    'Item / Service / Offering', 'Qty', 'Unit Price (INR)', 'Total Amount (INR)',
    'Payment ID', 'Status',
  ];

  const rows = transactions.map(tx => {
    const dtStr   = fmtDateTime(tx._date || tx.createdAt);
    const txId    = safeStr(String(tx._id || '').slice(-10).toUpperCase() || 'N/A');
    const type    = getTypeBadge(tx._source).label;
    const status  = safeStr(tx._status || 'N/A');


    if (tx._source === 'donation') {
      const subType   = tx.donationType === 'cash_gift' ? 'Cash Gift' : 'Shop Donation';
      const devotee   = safeStr(tx.donorName || tx.userId?.name || 'Anonymous');
      const email     = safeStr(tx.donorEmail || '');
      const phone     = safeStr(tx.donorPhone || '');
      const offering  = safeStr(tx.offeringName || 'Devotional Contribution');
      const paymentId = safeStr(tx.razorpayPaymentId || tx.razorpayOrderId || '');
      const amount    = tx.amount || tx._amount || 0;
      return [dtStr, txId, type, subType, devotee, email, phone, offering, 1, amount, amount, paymentId, status];
    }

    if (tx._source === 'activity') {
      const devotee   = safeStr(tx.customerName || tx.userId?.name || 'Devotee');
      const email     = safeStr(tx.userId?.email || '');
      const phone     = safeStr(tx.customerPhone || tx.phone || '');
      const service   = safeStr(tx.serviceName || tx.ceremonyName || 'Spiritual Offering');
      const paymentId = safeStr(tx.razorpayPaymentId || tx.paymentId || '');
      const amount    = tx.price || tx.amount || tx._amount || 0;
      return [dtStr, txId, type, 'Ceremony / Activity', devotee, email, phone, service, 1, amount, amount, paymentId, status];
    }

    // Order
    const devotee   = safeStr(tx.userId?.name || tx.customerName || 'Customer');
    const email     = safeStr(tx.userId?.email || '');
    const phone     = safeStr(tx.userId?.phone || tx.phone || '');
    const product   = safeStr(tx.productName || tx.productId?.name || 'Marketplace Item');
    const qty       = tx.quantity || 1;
    const unitPrice = tx.unitPrice || (qty > 0 ? Math.round((tx.totalAmount || 0) / qty) : 0);
    const total     = tx.totalAmount || tx._amount || 0;
    const paymentId = safeStr(tx.razorpayPaymentId || tx.paymentId || '');
    return [dtStr, txId, type, 'Marketplace', devotee, email, phone, product, qty, unitPrice, total, paymentId, status];
  });

  // Summary footer
  const donTotal = transactions.filter(t => t._source === 'donation').reduce((s, t) => s + (t._amount || 0), 0);
  const actTotal = transactions.filter(t => t._source === 'activity').reduce((s, t) => s + (t._amount || 0), 0);
  const ordTotal = transactions.filter(t => t._source === 'order').reduce((s, t) => s + (t._amount || 0), 0);
  const grandTotal = donTotal + actTotal + ordTotal;

  const footer = [
    [],
    ['--- SUMMARY ---'],
    ['Centre', safeStr(centreName)],
    ['Period', preset.toUpperCase()],
    ['Total Donations (INR)', donTotal],
    ['Total Offerings / Ceremonies (INR)', actTotal],
    ['Total Orders (INR)', ordTotal],
    ['Grand Total (INR)', grandTotal],
    ['Total Transactions', transactions.length],
    ['Generated On', new Date().toLocaleString('en-IN')],
  ];

  const allRows = [header, ...rows, ...footer];
  const csv = allRows.map(r => r.map(c => `"${safeStr(c)}"`).join(',')).join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `${(centreName || 'Centre').replace(/\s+/g, '_')}_Revenue_${preset}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────────────────────────────────────
// Stat Mini-Card
// ─────────────────────────────────────────────────────────────────────────────
const MiniStat = ({ label, value, color }) => (
  <div className="rounded-2xl p-4"
    style={{ background: `${color}08`, border: `1px solid ${color}18` }}>
    <p className="text-[9px] font-black uppercase tracking-wider mb-1"
      style={{ color: `${color}80` }}>{label}</p>
    <p className="text-xl font-bold" style={{ color: '#FDF6E3' }}>{value}</p>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// PRESETS
// ─────────────────────────────────────────────────────────────────────────────
const PRESETS = [
  { key: 'today', label: 'Today'       },
  { key: 'week',  label: 'Last 7 Days' },
  { key: 'month', label: 'This Month'  },
  { key: 'year',  label: 'This Year'   },
  { key: 'all',   label: 'All Time'    },
];

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────
const CentreRevenue = () => {
  const [preset, setPreset]             = useState('month');
  const [loading, setLoading]           = useState(true);
  const [allDonations, setAllDonations] = useState([]);
  const [allActivities, setAllActivities] = useState([]);
  const [allOrders, setAllOrders]       = useState([]);
  const [centreName, setCentreName]     = useState('Your Centre');

  // ── Single fetch on mount ──────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [centreRes, donRes, actRes, ordRes] = await Promise.allSettled([
        api.get('/admin/centres'),
        api.get('/donations'),
        api.get('/spiritual-activities/for-centre'),
        api.get('/admin/orders'),
      ]);

      if (centreRes.status === 'fulfilled') {
        setCentreName(centreRes.value.data?.data?.[0]?.name || 'Your Centre');
      }
      if (donRes.status === 'fulfilled') {
        setAllDonations(donRes.value.data?.data || []);
      }
      if (actRes.status === 'fulfilled') {
        setAllActivities(actRes.value.data?.data || []);
      }
      if (ordRes.status === 'fulfilled') {
        setAllOrders(ordRes.value.data?.data || []);
      }
    } catch (e) {
      console.error('CentreRevenue fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Date filter ─────────────────────────────────────────────────────────────
  const { start, end } = getDateRange(preset);

  const filteredDonations  = filterByDate(allDonations,  'createdAt', start, end);
  const filteredActivities = filterByDate(allActivities, 'createdAt', start, end);
  const filteredOrders     = filterByDate(allOrders.filter(o => (o.status || '').toLowerCase() !== 'cancelled'), 'createdAt', start, end);

  // ── Totals ──────────────────────────────────────────────────────────────────
  const donationTotal  = filteredDonations.reduce((s, d)  => s + (d.amount     || 0), 0);
  const activityTotal  = filteredActivities.reduce((s, a) => s + (a.price      || 0), 0);
  const orderTotal     = filteredOrders.reduce((s, o)     => s + (o.totalAmount || 0), 0);
  const devotionTotal  = donationTotal + activityTotal; // Donation Stream total
  const grandTotal     = devotionTotal + orderTotal;

  // ── Split Donation collection into sub-types ─────────────────────────────
  // Cash Gifts     = Custom Contribution (user typed a custom amount)
  // Shop Donations = Centre shop item donated to the centre (not delivered)
  // Uses the donationType field now persisted in MongoDB.
  // Old records (no donationType) land in shopDonations by default.
  const cashGifts     = filteredDonations.filter(d => d.donationType === 'cash_gift');
  const shopDonations = filteredDonations.filter(d => d.donationType !== 'cash_gift');

  // ── Top items ──────────────────────────────────────────────────────────────
  const allDevotionItems = [
    ...filteredDonations.map(d => ({ name: d.offeringName || 'Cash Gift', val: d.amount || 0 })),
    ...filteredActivities.map(a => ({ name: a.serviceName || 'Offering',   val: a.price || 0 })),
  ];
  const offeringMap = allDevotionItems.reduce((acc, x) => {
    acc[x.name] = (acc[x.name] || 0) + x.val;
    return acc;
  }, {});
  const topOffering = Object.entries(offeringMap).sort((a, b) => b[1] - a[1])[0];

  const productMap = filteredOrders.reduce((acc, o) => {
    const k = o.productName || o.productId?.name || 'Product';
    acc[k] = (acc[k] || 0) + (o.totalAmount || 0);
    return acc;
  }, {});
  const topProduct = Object.entries(productMap).sort((a, b) => b[1] - a[1])[0];

  // ── Unique devotees ────────────────────────────────────────────────────────
  const uniqueDevotees = new Set([
    ...filteredDonations.map(d  => d.donorEmail || d.userId?._id || d.userId).filter(Boolean),
    ...filteredActivities.map(a => a.userId?._id || a.userId).filter(Boolean),
    ...filteredOrders.map(o     => o.userId?._id || o.userId).filter(Boolean),
  ]).size;

  // ── Unified transaction ledger (all three streams) ─────────────────────────
  const transactions = [
    ...filteredDonations.map(d => ({
      _id:     d._id,
      _source: 'donation',
      _date:   d.createdAt,
      _devotee: d.donorName || d.userId?.name || 'Devotee',
      _amount: d.amount || 0,
      _status: d.status === 'completed' ? 'Settled' : (d.status || 'Pending'),
      ...d,
    })),
    ...filteredActivities.map(a => ({
      _id:     a._id,
      _source: 'activity',
      _date:   a.createdAt,
      _devotee: a.customerName || a.userId?.name || 'Devotee',
      _amount: a.price || 0,
      _status: a.status || 'Completed',
      ...a,
    })),
    ...filteredOrders.map(o => ({
      _id:     o._id,
      _source: 'order',
      _date:   o.createdAt,
      _devotee: o.userId?.name || 'Customer',
      _amount: o.totalAmount || 0,
      _status: o.status || 'Ordered',
      ...o,
    })),
  ].sort((a, b) => new Date(b._date) - new Date(a._date));

  // ── Daily chart data ───────────────────────────────────────────────────────
  const buildDailyChart = () => {
    const days = {};
    const dayKey = (dateStr) =>
      new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

    const addDay = (dateStr, key, val) => {
      const label = dayKey(dateStr);
      if (!days[label]) days[label] = { date: label, Donations: 0, Orders: 0, _ts: new Date(dateStr).getTime() };
      days[label][key] += val;
    };

    filteredDonations.forEach(d  => addDay(d.createdAt,  'Donations', d.amount     || 0));
    filteredActivities.forEach(a => addDay(a.createdAt,  'Donations', a.price      || 0));
    filteredOrders.forEach(o     => addDay(o.createdAt,  'Orders',    o.totalAmount || 0));

    return Object.values(days).sort((a, b) => a._ts - b._ts);
  };
  const chartData = buildDailyChart();

  // ─────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center"
        style={{ background: '#120505' }}>
        <div className="text-center">
          <div className="w-16 h-16 rounded-full border-4 border-[#E8B84B]/20 border-t-[#E8B84B] animate-spin mx-auto mb-6" />
          <p className="text-[#E8B84B] tracking-widest text-sm font-bold">LOADING LEDGER...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20"
      style={{ background: 'linear-gradient(160deg, #120505 0%, #1a0808 50%, #0e0404 100%)' }}>

      {/* ════════════════════════════════════════════════════════════════════
          ZONE A — STICKY SACRED SUM HEADER
      ════════════════════════════════════════════════════════════════════ */}
      <div className="sticky top-0 z-30 px-8 py-5"
        style={{
          background: 'rgba(18,5,5,0.93)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(212,175,55,0.13)',
        }}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">

          {/* Left — Title + Animated Counter */}
          <div className="flex items-end gap-5">
            <div>
              <p className="text-[9px] font-black uppercase tracking-[0.35em] mb-0.5"
                style={{ color: 'rgba(212,175,55,0.5)' }}>
                {centreName} · Divine Ledger
              </p>
              <div className="font-serif text-4xl font-black" style={{ color: '#E8B84B' }}>
                <AnimatedCounter target={grandTotal} />
              </div>
              <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.25)' }}>
                Total Revenue · {PRESETS.find(p => p.key === preset)?.label}
              </p>
            </div>

            {/* Quick split badges */}
            <div className="hidden md:flex items-center gap-2 mb-1">
              <span className="text-[10px] px-2.5 py-1 rounded-lg font-bold"
                style={{ background: 'rgba(212,175,55,0.1)', color: '#E8B84B', border: '1px solid rgba(212,175,55,0.2)' }}>
                🟡 ₹{fmt(devotionTotal)} Donations
              </span>
              <span className="text-[10px] px-2.5 py-1 rounded-lg font-bold"
                style={{ background: 'rgba(34,197,94,0.08)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.2)' }}>
                🟢 ₹{fmt(orderTotal)} Orders
              </span>
            </div>
          </div>

          {/* Right — Preset Selector + Export */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex rounded-xl overflow-hidden"
              style={{ border: '1px solid rgba(212,175,55,0.18)' }}>
              {PRESETS.map(p => (
                <button
                  key={p.key}
                  onClick={() => setPreset(p.key)}
                  className="px-3 py-1.5 text-[10px] font-black uppercase tracking-wider transition-all"
                  style={{
                    background: preset === p.key ? 'rgba(212,175,55,0.18)' : 'transparent',
                    color: preset === p.key ? '#E8B84B' : 'rgba(212,175,55,0.3)',
                  }}>
                  {p.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => exportCSV(transactions, centreName, preset)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-[11px] font-black uppercase tracking-wider transition-all hover:opacity-80 active:scale-95"
              style={{
                border: '1px solid rgba(212,175,55,0.35)',
                color: '#E8B84B',
                background: 'rgba(212,175,55,0.07)',
              }}>
              <span className="material-symbols-outlined text-[16px]">download</span>
              Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="px-8 pt-8 space-y-8">

        {/* ══════════════════════════════════════════════════════════════════
            ZONE B — TWO-STREAM INCOME FEATURE BLOCKS
        ══════════════════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* ─── Block 1: Donation Stream (Gold) ─── */}
          <div className="relative rounded-3xl p-7 overflow-hidden"
            style={{
              background: 'rgba(212,175,55,0.03)',
              border: '1px solid rgba(212,175,55,0.22)',
              backdropFilter: 'blur(12px)',
            }}>
            <div className="absolute -bottom-10 -right-10 w-56 h-56 rounded-full pointer-events-none opacity-[0.07]"
              style={{ background: '#E8B84B', filter: 'blur(50px)' }} />

            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: '#E8B84B' }} />
                  <span className="text-[9px] font-black uppercase tracking-[0.3em]" style={{ color: '#E8B84B' }}>
                    Donation Stream
                  </span>
                </div>
                <div className="font-serif text-5xl font-black leading-none" style={{ color: '#FDF6E3' }}>
                  ₹{fmt(devotionTotal)}
                </div>
                <p className="text-xs mt-1" style={{ color: 'rgba(212,175,55,0.45)' }}>
                  Custom contributions + Shop donations + Ritual offerings
                </p>
              </div>
              <div className="rounded-2xl flex items-center justify-center shrink-0"
                style={{ background: 'rgba(212,175,55,0.1)', width: 52, height: 52 }}>
                <span className="material-symbols-outlined text-[26px]" style={{ color: '#E8B84B' }}>volunteer_activism</span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-2">
              <MiniStat label="Cash Gifts"      value={cashGifts.length}          color="#E8B84B" />
              <MiniStat label="Shop Donations"  value={shopDonations.length}      color="#f59e0b" />
              <MiniStat label="Offerings"        value={filteredActivities.length} color="#a78bfa" />
            </div>

            {topOffering && (
              <div className="mt-3 px-3 py-2 rounded-xl text-[10px]"
                style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.1)' }}>
                🏆 Top: <span style={{ color: '#E8B84B', fontWeight: 700 }}>{topOffering[0]}</span>
                <span style={{ color: 'rgba(255,255,255,0.4)' }}> — ₹{fmt(topOffering[1])}</span>
              </div>
            )}
          </div>

          {/* ─── Block 2: Marketplace Stream (Green) ─── */}
          <div className="relative rounded-3xl p-7 overflow-hidden"
            style={{
              background: 'rgba(34,197,94,0.025)',
              border: '1px solid rgba(34,197,94,0.18)',
              backdropFilter: 'blur(12px)',
            }}>
            <div className="absolute -bottom-10 -right-10 w-56 h-56 rounded-full pointer-events-none opacity-[0.06]"
              style={{ background: '#22c55e', filter: 'blur(50px)' }} />

            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: '#22c55e' }} />
                  <span className="text-[9px] font-black uppercase tracking-[0.3em]" style={{ color: '#22c55e' }}>
                    Marketplace Stream
                  </span>
                </div>
                <div className="font-serif text-5xl font-black leading-none" style={{ color: '#FDF6E3' }}>
                  ₹{fmt(orderTotal)}
                </div>
                <p className="text-xs mt-1" style={{ color: 'rgba(34,197,94,0.45)' }}>
                  Centre shop + Divine Essentials delivery
                </p>
              </div>
              <div className="rounded-2xl flex items-center justify-center shrink-0"
                style={{ background: 'rgba(34,197,94,0.1)', width: 52, height: 52 }}>
                <span className="material-symbols-outlined text-[26px]" style={{ color: '#22c55e' }}>shopping_bag</span>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-2">
              <MiniStat label="Total Orders" value={filteredOrders.length} color="#22c55e" />
              <MiniStat label="Pending"
                value={filteredOrders.filter(o => !['Delivered','Cancelled'].includes(o.status)).length}
                color="#eab308" />
              <MiniStat label="Delivered"
                value={filteredOrders.filter(o => o.status === 'Delivered').length}
                color="#22c55e" />
            </div>

            {topProduct && (
              <div className="mt-3 px-3 py-2 rounded-xl text-[10px]"
                style={{ background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.1)' }}>
                🏆 Top Seller: <span style={{ color: '#22c55e', fontWeight: 700 }}>{topProduct[0]}</span>
                <span style={{ color: 'rgba(255,255,255,0.4)' }}> — ₹{fmt(topProduct[1])}</span>
              </div>
            )}
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            CHART + DEVOTEE IMPACT SIDEBAR
        ══════════════════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* Daily Bar Chart */}
          <div className="lg:col-span-4 rounded-3xl p-6"
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(212,175,55,0.1)',
              backdropFilter: 'blur(12px)',
            }}>
            <div className="mb-5">
              <h3 className="font-serif font-bold" style={{ color: '#FDF6E3' }}>
                Donations vs Orders — Daily Breakdown
              </h3>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(212,175,55,0.4)' }}>
                Revenue by day for {PRESETS.find(p => p.key === preset)?.label}
              </p>
            </div>

            {chartData.length === 0 ? (
              <EmptyLedger />
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }} barGap={3}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(212,175,55,0.05)" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: 'rgba(212,175,55,0.4)', fontSize: 9, fontWeight: 700 }}
                    axisLine={false} tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: 'rgba(212,175,55,0.3)', fontSize: 9 }}
                    axisLine={false} tickLine={false}
                    tickFormatter={v => {
                      if (v === 0) return '';
                      if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
                      if (v >= 1000)   return `₹${(v / 1000).toFixed(0)}k`;
                      return `₹${v}`;
                    }}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.025)' }} />
                  <Legend
                    wrapperStyle={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.4)', paddingTop: 14 }}
                    iconType="circle" iconSize={8}
                  />
                  <Bar dataKey="Donations" fill="#E8B84B" radius={[4, 4, 0, 0]} maxBarSize={36} />
                  <Bar dataKey="Orders"    fill="#22c55e" radius={[4, 4, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Devotee Impact Sidebar */}
          <div className="lg:col-span-1 rounded-3xl p-5 flex flex-col gap-5"
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(167,139,250,0.18)',
              backdropFilter: 'blur(12px)',
            }}>

            {/* Unique devotees */}
            <div>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                style={{ background: 'rgba(167,139,250,0.1)' }}>
                <span className="material-symbols-outlined text-[20px]" style={{ color: '#a78bfa' }}>groups</span>
              </div>
              <p className="text-[9px] font-black uppercase tracking-widest mb-0.5"
                style={{ color: 'rgba(167,139,250,0.55)' }}>Devotee Reach</p>
              <p className="font-serif text-3xl font-black" style={{ color: '#FDF6E3' }}>{uniqueDevotees}</p>
              <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.25)' }}>unique devotees</p>
            </div>

            <div className="h-px" style={{ background: 'rgba(167,139,250,0.08)' }} />

            {/* Settlement status */}
            <div>
              <p className="text-[9px] font-black uppercase tracking-widest mb-2"
                style={{ color: 'rgba(212,175,55,0.45)' }}>Settlement</p>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black"
                style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.18)' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                Settled to Trust
              </span>
            </div>

            <div className="h-px" style={{ background: 'rgba(167,139,250,0.08)' }} />

            {/* Income split bars */}
            <div>
              <p className="text-[9px] font-black uppercase tracking-widest mb-3"
                style={{ color: 'rgba(212,175,55,0.45)' }}>Income Split</p>
              {grandTotal > 0 ? (
                <>
                  {[
                    { label: 'Donations', val: devotionTotal, color: '#E8B84B' },
                    { label: 'Orders',    val: orderTotal,    color: '#22c55e' },
                  ].map(({ label, val, color }) => {
                    const pct = Math.round((val / grandTotal) * 100);
                    return (
                      <div key={label} className="mb-3">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex-1 h-1.5 rounded-full overflow-hidden"
                            style={{ background: 'rgba(255,255,255,0.05)' }}>
                            <div className="h-full rounded-full transition-all duration-700"
                              style={{ width: `${pct}%`, background: color }} />
                          </div>
                          <span className="text-[9px] font-bold w-7 text-right" style={{ color }}>
                            {pct}%
                          </span>
                        </div>
                        <p className="text-[9px]" style={{ color: `${color}70` }}>{label}</p>
                      </div>
                    );
                  })}
                </>
              ) : (
                <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.2)' }}>No data yet</p>
              )}
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            ZONE C — TRANSACTION LEDGER TABLE
        ══════════════════════════════════════════════════════════════════ */}
        <div className="rounded-3xl overflow-hidden"
          style={{
            background: 'rgba(255,255,255,0.015)',
            border: '1px solid rgba(212,175,55,0.1)',
          }}>

          {/* Ledger header bar */}
          <div className="px-6 py-5 flex items-center justify-between flex-wrap gap-3"
            style={{ borderBottom: '1px solid rgba(212,175,55,0.08)' }}>
            <div>
              <h3 className="font-serif font-bold" style={{ color: '#FDF6E3' }}>
                Transaction Ledger
              </h3>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(212,175,55,0.4)' }}>
                {transactions.length} records · {PRESETS.find(p => p.key === preset)?.label}
              </p>
            </div>
            <div className="flex items-center gap-4 text-[10px] font-bold"
              style={{ color: 'rgba(255,255,255,0.3)' }}>
              <span>🟡 Donation</span>
              <span>🟣 Offering</span>
              <span>🟢 Order</span>
            </div>
          </div>

          {transactions.length === 0 ? (
            <EmptyLedger full />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr style={{
                      background: 'rgba(212,175,55,0.04)',
                      borderBottom: '1px solid rgba(212,175,55,0.07)',
                    }}>
                      {['Date', 'TX ID', 'Source', 'Devotee / Customer', 'Item / Service', 'Status', 'Amount'].map(h => (
                        <th key={h} className="px-5 py-4 text-[9px] font-black uppercase tracking-widest"
                          style={{ color: 'rgba(212,175,55,0.45)' }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx, i) => {
                      const badge  = getTypeBadge(tx._source);
                      const stStyle = getStatusStyle(tx._status);
                      return (
                        <tr key={tx._id || i}
                          style={{
                            background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.008)',
                            borderBottom: '1px solid rgba(255,255,255,0.025)',
                          }}
                          className="hover:bg-white/[0.015] transition-colors">

                          {/* Date */}
                          <td className="px-5 py-3.5">
                            <div className="text-xs font-bold" style={{ color: 'rgba(255,255,255,0.6)' }}>
                              {new Date(tx._date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
                            </div>
                            <div className="text-[9px]" style={{ color: 'rgba(255,255,255,0.22)' }}>
                              {new Date(tx._date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </td>

                          {/* TX ID */}
                          <td className="px-5 py-3.5">
                            <span className="font-mono text-[10px] px-2 py-1 rounded-lg"
                              style={{ background: 'rgba(212,175,55,0.07)', color: 'rgba(212,175,55,0.6)' }}>
                              #{(tx._id || '').slice(-8).toUpperCase() || 'N/A'}
                            </span>
                          </td>

                          {/* Source */}
                          <td className="px-5 py-3.5">
                            <span className="text-[10px] px-2.5 py-1 rounded-lg font-black"
                              style={{ background: badge.bg, color: badge.color }}>
                              {badge.dot} {badge.label}
                            </span>
                          </td>

                          {/* Devotee */}
                          <td className="px-5 py-3.5">
                            <div className="text-xs font-bold" style={{ color: 'rgba(255,255,255,0.7)' }}>
                              {tx._devotee}
                            </div>
                            {tx.donorEmail && (
                              <div className="text-[9px]" style={{ color: 'rgba(255,255,255,0.22)' }}>
                                {tx.donorEmail}
                              </div>
                            )}
                          </td>

                          {/* Item */}
                          <td className="px-5 py-3.5 max-w-[160px]">
                            <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.45)' }}>
                              {getTypeLabel(tx)}
                            </span>
                          </td>

                          {/* Status */}
                          <td className="px-5 py-3.5">
                            <span className="text-[10px] font-black px-2 py-1 rounded-lg"
                              style={stStyle}>
                              {tx._status}
                            </span>
                          </td>

                          {/* Amount */}
                          <td className="px-5 py-3.5">
                            <span className="font-serif font-black text-sm" style={{ color: badge.color }}>
                              ₹{fmt(tx._amount)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer total */}
              <div className="flex items-center justify-between px-6 py-4"
                style={{ borderTop: '1px solid rgba(212,175,55,0.1)', background: 'rgba(212,175,55,0.03)' }}>
                <div className="flex items-center gap-6">
                  <span className="text-[10px] font-black uppercase tracking-widest"
                    style={{ color: 'rgba(212,175,55,0.45)' }}>
                    {transactions.length} Transactions
                  </span>
                  <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.25)' }}>
                    🟡 ₹{fmt(devotionTotal)} Donations
                    &nbsp;&nbsp;🟢 ₹{fmt(orderTotal)} Orders
                  </span>
                </div>
                <span className="font-serif font-black text-xl" style={{ color: '#E8B84B' }}>
                  ₹{fmt(grandTotal)}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Empty State
// ─────────────────────────────────────────────────────────────────────────────
const EmptyLedger = ({ full }) => (
  <div className={`flex flex-col items-center justify-center text-center ${full ? 'py-24' : 'py-16'}`}>
    <span className="text-7xl mb-4" style={{ opacity: 0.1 }}>🏺</span>
    <p className="text-sm font-bold mb-1" style={{ color: 'rgba(212,175,55,0.35)' }}>
      The centre's ledger is currently awaiting its first divine contribution.
    </p>
    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.12)' }}>
      Try selecting a different date range.
    </p>
  </div>
);

export default CentreRevenue;
